import { useTranslation } from "react-i18next";
import type { AddressResponse } from "../../api/addresses/types";
import { deliveryMatchesSelectedFlyerType, type DeliveryResponse } from "../../api/deliveries";

/** Same rule for showing "Ångra utdelning" in context menu and bulk panel. */
export function canUndoAddressMapDelivery(
    selectedFlyerTypeId: string | null | undefined,
        onUndoDelivery: (() => void | Promise<void>) | undefined,
    deliveries: DeliveryResponse[],
    currentUserId: string | undefined
): boolean {
    return !!(
        selectedFlyerTypeId &&
        onUndoDelivery &&
        deliveries.some(
            (d) =>
                deliveryMatchesSelectedFlyerType(d, selectedFlyerTypeId) &&
                !d.isNoThanks &&
                (d.quantity ?? 0) > 0 &&
                (!currentUserId || d.recordedByUserId === currentUserId)
        )
    );
}

export function canUndoAddressMapNoThanks(
    selectedFlyerTypeId: string | null | undefined,
    onUndoNoThanks: (() => void | Promise<void>) | undefined,
    deliveries: DeliveryResponse[],
    currentUserId: string | undefined
): boolean {
    return !!(
        selectedFlyerTypeId &&
        onUndoNoThanks &&
        deliveries.some(
            (d) =>
                deliveryMatchesSelectedFlyerType(d, selectedFlyerTypeId) &&
                d.isNoThanks &&
                (!currentUserId || d.recordedByUserId === currentUserId)
        )
    );
}

export function canMarkAddressMapNoThanks(
    selectedFlyerTypeId: string | null | undefined,
    address: AddressResponse,
    deliveries: DeliveryResponse[]
): boolean {
    if (!selectedFlyerTypeId || address.isBlocked) {
        return false;
    }
    const match = deliveries.filter((d) => deliveryMatchesSelectedFlyerType(d, selectedFlyerTypeId));
    const hasRealDelivery = match.some((d) => !d.isNoThanks && (d.quantity ?? 0) > 0);
    const hasNoThanks = match.some((d) => d.isNoThanks);
    return !hasRealDelivery && !hasNoThanks;
}

export function canConvertAddressMapDeliveryToNoThanks(
    selectedFlyerTypeId: string | null | undefined,
    address: AddressResponse,
    deliveries: DeliveryResponse[],
    currentUserId: string | undefined
): boolean {
    if (!selectedFlyerTypeId || address.isBlocked) {
        return false;
    }
    return deliveries.some(
        (d) =>
            deliveryMatchesSelectedFlyerType(d, selectedFlyerTypeId) &&
            !d.isNoThanks &&
            (d.quantity ?? 0) > 0 &&
            (!currentUserId || d.recordedByUserId === currentUserId)
    );
}

export function canMarkAddressMapDelivered(
    address: AddressResponse,
    deliveries: DeliveryResponse[],
    selectedFlyerTypeId: string | null | undefined
): boolean {
    if (address.isBlocked) {
        return false;
    }
    if (!selectedFlyerTypeId) {
        return true;
    }
    const match = deliveries.filter((d) => deliveryMatchesSelectedFlyerType(d, selectedFlyerTypeId));
    const hasRealDelivery = match.some((d) => !d.isNoThanks && (d.quantity ?? 0) > 0);
    return !hasRealDelivery;
}

export function buildAddressGoogleMapsSearchUrl(address: AddressResponse): string {
    const query = [address.street, address.number, address.apartment, address.postalCode, address.city]
        .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
        .join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export type AddressMapActionListVariant = "context-menu" | "panel";

export interface AddressMapActionListProps {
    variant: AddressMapActionListVariant;
    address: AddressResponse;
    googleMapsUrl: string;
    isLoading: boolean;
    markDeliveredDisabled: boolean;
    canUndo: boolean;
    canUndoNoThanks?: boolean;
    showMarkDelivered?: boolean;
    showMarkNoThanks?: boolean;
    showConvertToNoThanks?: boolean;
    /** Called after most actions (close context menu or clear panel selection). */
    dismissAfterAction: () => void;
    /** When true, delivery history does not dismiss the host (panel stays open). */
    keepHostOpenOnDeliveryHistory: boolean;
    /** Context menu closes after mark; bulk panel keeps selection until confirm (opens dialog first). */
    dismissAfterMarkDelivered: boolean;
    onDeliveryHistory?: () => void;
    onMarkDelivered?: () => void;
    onMarkNoThanks?: () => void;
    onConvertToNoThanks?: () => void | Promise<void>;
    onUndoDelivery?: () => void | Promise<void>;
    onUndoNoThanks?: () => void | Promise<void>;
    onHide?: () => void;
    onBlock?: () => void;
    onMove?: () => void;
    onEdit?: () => void;
    onCopy?: () => void;
}

/**
 * Shared address actions for the map context menu and the single-address bulk panel.
 * Order and visibility rules stay in one place.
 */
export const AddressMapActionList = ({
    variant,
    address,
    googleMapsUrl,
    isLoading,
    markDeliveredDisabled,
    canUndo,
    canUndoNoThanks = false,
    showMarkDelivered = true,
    showMarkNoThanks = false,
    showConvertToNoThanks = false,
    dismissAfterAction,
    keepHostOpenOnDeliveryHistory,
    dismissAfterMarkDelivered,
    onDeliveryHistory,
    onMarkDelivered,
    onMarkNoThanks,
    onConvertToNoThanks,
    onUndoDelivery,
    onUndoNoThanks,
    onHide,
    onBlock,
    onMove,
    onEdit,
    onCopy
}: AddressMapActionListProps): JSX.Element => {
    const { t } = useTranslation();

    const btnClass =
        variant === "context-menu" ? "address-context-menu__item" : "bulk-address-panel__context-button";
    const linkClass =
        variant === "context-menu"
            ? "address-context-menu__item address-context-menu__item--link"
            : "bulk-address-panel__context-button bulk-address-panel__context-button--link";

    const fire = (fn: () => void) => {
        fn();
        dismissAfterAction();
    };

    return (
        <>
            {onDeliveryHistory && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        onDeliveryHistory();
                        if (!keepHostOpenOnDeliveryHistory) {
                            dismissAfterAction();
                        }
                    }}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.deliveryHistory")}
                </button>
            )}

            {showMarkDelivered && onMarkDelivered && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        onMarkDelivered();
                        if (dismissAfterMarkDelivered) {
                            dismissAfterAction();
                        }
                    }}
                    disabled={isLoading || markDeliveredDisabled}
                >
                    {t("map.addressContextMenu.markDelivered")}
                </button>
            )}

            {showMarkNoThanks && onMarkNoThanks && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        onMarkNoThanks();
                        if (dismissAfterMarkDelivered) {
                            dismissAfterAction();
                        }
                    }}
                    disabled={isLoading || markDeliveredDisabled}
                >
                    {t("map.addressContextMenu.markNoThanks")}
                </button>
            )}

            {showConvertToNoThanks && onConvertToNoThanks && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        void Promise.resolve(onConvertToNoThanks()).finally(() => dismissAfterAction());
                    }}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.convertToNoThanks")}
                </button>
            )}

            {canUndo && onUndoDelivery && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        void Promise.resolve(onUndoDelivery()).finally(() => dismissAfterAction());
                    }}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.undoDelivery")}
                </button>
            )}

            {canUndoNoThanks && onUndoNoThanks && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => {
                        void Promise.resolve(onUndoNoThanks()).finally(() => dismissAfterAction());
                    }}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.undoNoThanks")}
                </button>
            )}

            {onHide && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onHide)}
                    disabled={isLoading}
                >
                    {address.isHidden
                        ? t("map.addressContextMenu.showAddress")
                        : t("map.addressContextMenu.hideAddress")}
                </button>
            )}

            {onBlock && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onBlock)}
                    disabled={isLoading}
                >
                    {address.isBlocked
                        ? t("map.addressContextMenu.unblockAddress")
                        : t("map.addressContextMenu.blockAddress")}
                </button>
            )}

            {onMove && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onMove)}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.moveAddress")}
                </button>
            )}

            {onEdit && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onEdit)}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.editMetadata")}
                </button>
            )}

            {onCopy && (
                <button
                    type="button"
                    className={btnClass}
                    onClick={() => fire(onCopy)}
                    disabled={isLoading}
                >
                    {t("map.addressContextMenu.copyAddress")}
                </button>
            )}

            <a
                className={linkClass}
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismissAfterAction}
            >
                {t("map.addressContextMenu.openInGoogleMaps")}
            </a>
        </>
    );
};
